import { useId } from "react";

interface AssistantMascotProps {
  className?: string;
  size?: number;
  title?: string;
  animated?: boolean;
  hoverInspect?: boolean;
  inspect?: boolean;
  hover?: boolean;
  glow?: boolean;
}

export function AssistantMascot({
  className,
  size,
  title,
  animated = true,
  inspect = false,
  hover = false,
  hoverInspect = true,
  glow = true,
}: AssistantMascotProps) {
  const uid = `m${useId().replace(/:/g, "")}`;
  const k = (n: string) => `${uid}-${n}`;
  const url = (n: string) => `url(#${k(n)})`;
  const lifted = (n: string) =>
    `${hoverInspect ? `[data-m="${uid}"]:hover .${k(n)}, ` : ""}[data-m="${uid}"].hovering .${k(n)}, [data-m="${uid}"].inspecting .${k(n)}`;

  const css = `
[data-m="${uid}"] .${k("float")} { animation: ${k("float")} 4.2s ease-in-out infinite; }
[data-m="${uid}"] .${k("eyes")} { animation: ${k("blink")} 5.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
[data-m="${uid}"] .${k("orb")} { animation: ${k("pulse")} 2.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
[data-m="${uid}"] .${k("core")} { animation: ${k("pulse")} 3.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
[data-m="${uid}"] .${k("halo")} { animation: ${k("halo")} 5.5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }

[data-m="${uid}"] .${k("armup")} { transform-box: view-box; transform-origin: 65px 63px; transform: rotate(140deg); transition: transform .72s cubic-bezier(.32,.84,.3,1); }
[data-m="${uid}"] .${k("armfore")} { transform-box: view-box; transform-origin: 73.8px 52.6px; transform: rotate(-196deg); transition: transform .82s cubic-bezier(.34,1.04,.42,1) .05s; }
[data-m="${uid}"] .${k("mageye")} { opacity: 0; transform-box: fill-box; transform-origin: center; transform: scale(.55); transition: opacity .3s ease .22s, transform .5s cubic-bezier(.34,1.45,.5,1) .18s; }
[data-m="${uid}"] .${k("fx")} { opacity: 0; transition: opacity .4s ease .2s; }
[data-m="${uid}"] .${k("fxring")} { transform-box: fill-box; transform-origin: center; animation: ${k("spin")} 3.4s linear infinite; }
[data-m="${uid}"] .${k("fxring2")} { transform-box: fill-box; transform-origin: center; animation: ${k("spinr")} 5.2s linear infinite; }
[data-m="${uid}"] .${k("fxscan")} { animation: ${k("scanline")} 1.6s ease-in-out infinite; }
[data-m="${uid}"] .${k("fxshine")} { animation: ${k("shine")} 2.2s ease-in-out infinite; }
[data-m="${uid}"] .${k("fxarc")} { opacity: 0; transition: opacity .35s ease .12s; transform-box: fill-box; transform-origin: 61px 43px; animation: ${k("arcpulse")} 1.7s ease-in-out infinite; }

${lifted("armup")} { transform: rotate(0deg); }
${lifted("armfore")} { transform: rotate(0deg); }
${lifted("mageye")} { opacity: 1; transform: scale(1); }
${lifted("fxarc")} { opacity: 1; }
[data-m="${uid}"].inspecting .${k("fx")} { opacity: 1; }

@keyframes ${k("float")} { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
@keyframes ${k("spin")} { to { transform: rotate(360deg); } }
@keyframes ${k("spinr")} { to { transform: rotate(-360deg); } }
@keyframes ${k("blink")} { 0%,90%,100% { transform: scaleY(1); } 94%,96% { transform: scaleY(0.12); } }
@keyframes ${k("pulse")} { 0%,100% { opacity: .55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.12); } }
@keyframes ${k("halo")} { 0%,100% { opacity: .5; transform: scale(1); } 50% { opacity: .82; transform: scale(1.06); } }
@keyframes ${k("scanline")} { 0%,100% { transform: translateY(-6.5px); } 50% { transform: translateY(6.5px); } }
@keyframes ${k("shine")} { 0% { transform: translateX(-9px); opacity: 0; } 35% { opacity: .85; } 70% { opacity: .35; } 100% { transform: translateX(9px); opacity: 0; } }
@keyframes ${k("arcpulse")} { 0%,100% { transform: scale(.92); } 50% { transform: scale(1.06); } }
@media (prefers-reduced-motion: reduce) {
  [data-m="${uid}"] * { animation: none !important; }
  [data-m="${uid}"] .${k("armup")}, [data-m="${uid}"] .${k("armfore")} { transition: none !important; }
}
`;

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={`${className ?? ""}${inspect ? " inspecting" : ""}${hover ? " hovering" : ""}`}
      data-m={uid}
      role="img"
      aria-label={title ?? "Compliance Companion"}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {title ? <title>{title}</title> : null}
      {animated ? <style>{css}</style> : null}

      <defs>
        <radialGradient id={k("halo")} cx="50%" cy="48%" r="50%">
          <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.45" />
          <stop offset="55%" stopColor="#6D28D9" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#6D28D9" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={k("shadow")} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#2B2065" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#2B2065" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={k("shell")} x1="0" y1="0" x2="0.92" y2="1">
          <stop offset="0%" stopColor="#FBFBFF" />
          <stop offset="30%" stopColor="#E2E0F7" />
          <stop offset="62%" stopColor="#B2ADE2" />
          <stop offset="100%" stopColor="#6E68B6" />
        </linearGradient>
        <linearGradient id={k("shellDark")} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#BBB7E8" />
          <stop offset="100%" stopColor="#564E97" />
        </linearGradient>
        <linearGradient id={k("visor")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3D3089" />
          <stop offset="45%" stopColor="#251B5D" />
          <stop offset="100%" stopColor="#140F39" />
        </linearGradient>
        <radialGradient id={k("eye")} cx="38%" cy="32%" r="75%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="32%" stopColor="#C9FBFF" />
          <stop offset="62%" stopColor="#48D8EE" />
          <stop offset="100%" stopColor="#1AA3C6" />
        </radialGradient>
        <radialGradient id={k("eyeGlow")} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#67E8F9" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={k("coreg")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#C4B5FD" />
          <stop offset="52%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#882783" />
        </linearGradient>
        <radialGradient id={k("lens")} cx="36%" cy="32%" r="80%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.32" />
          <stop offset="38%" stopColor="#C4B5FD" stopOpacity="0.26" />
          <stop offset="72%" stopColor="#8B5CF6" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#6D4FC0" stopOpacity="0.42" />
        </radialGradient>
        <linearGradient id={k("lensRim")} x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FBFBFF" />
          <stop offset="45%" stopColor="#B4AFE4" />
          <stop offset="100%" stopColor="#665EAD" />
        </linearGradient>
        <linearGradient id={k("handle")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9C97D6" />
          <stop offset="50%" stopColor="#6A62B0" />
          <stop offset="100%" stopColor="#423B86" />
        </linearGradient>
        <linearGradient id={k("bubble")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2C2766" />
          <stop offset="100%" stopColor="#171140" />
        </linearGradient>
        <filter id={k("soft")} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.4" />
        </filter>
        <filter id={k("softer")} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
        <clipPath id={k("lensclip")}>
          <circle cx="61" cy="43" r="8.5" />
        </clipPath>
      </defs>

      {glow && <circle className={k("halo")} cx="54" cy="46" r="50" fill={url("halo")} />}
      <ellipse cx="58" cy="113" rx="40" ry="4.6" fill={url("shadow")} />

      <g className={k("float")}>
        <g>
          <rect x="78" y="15" width="24" height="16" rx="6" fill={url("bubble")} stroke="#4A4392" strokeWidth="0.7" />
          <path d="M83 30 L80.5 35 L88.5 30.5 Z" fill={url("bubble")} />
          <circle cx="84.5" cy="23" r="1.5" fill="#D7D3F4" />
          <circle cx="90" cy="23" r="1.5" fill="#D7D3F4" />
          <circle cx="95.5" cy="23" r="1.5" fill="#D7D3F4" />
        </g>

        <rect x="52.6" y="12" width="2.8" height="14" rx="1.4" fill={url("shellDark")} />
        <g className={k("orb")}>
          <circle cx="54" cy="10" r="5" fill={url("eyeGlow")} />
          <circle cx="54" cy="10" r="3" fill={url("eye")} />
          <circle cx="52.9" cy="8.9" r="0.9" fill="#FFFFFF" opacity="0.9" />
        </g>

        <g>
          <rect x="27.5" y="37" width="7" height="15" rx="3.5" fill={url("shellDark")} />
          <ellipse cx="31" cy="44.5" rx="1.9" ry="3.8" fill="#2A2160" opacity="0.85" />
          <circle cx="31" cy="44.5" r="1" fill="#67E8F9" opacity="0.7" />
          <rect x="78.5" y="37" width="7" height="15" rx="3.5" fill={url("shellDark")} />
          <ellipse cx="82" cy="44.5" rx="1.9" ry="3.8" fill="#2A2160" opacity="0.85" />
          <circle cx="82" cy="44.5" r="1" fill="#67E8F9" opacity="0.7" />
        </g>

        <rect x="49" y="57" width="12" height="9" rx="3.5" fill={url("shellDark")} />
        <line x1="43" y1="66" x2="38.5" y2="79" stroke={url("shellDark")} strokeWidth="8.5" strokeLinecap="round" />
        <circle cx="37.6" cy="82" r="4.7" fill={url("shell")} />
        <circle cx="36.3" cy="80.7" r="1.2" fill="#FFFFFF" opacity="0.3" />
        <rect x="39" y="60" width="30" height="26" rx="12" fill={url("shell")} />
        <path d="M60 61 C66 63 67 70 67 73 C67 80 64 84 58 85.5 C64 82 63 66 60 61 Z" fill="#4B4490" opacity="0.16" />
        <ellipse cx="42" cy="65.5" rx="5.4" ry="5" fill={url("shell")} />
        <ellipse cx="42" cy="67.2" rx="5" ry="3" fill="#4B4490" opacity="0.12" />
        <ellipse cx="66" cy="65.5" rx="5.4" ry="5" fill={url("shell")} />
        <ellipse cx="66" cy="67.2" rx="5" ry="3" fill="#4B4490" opacity="0.12" />

        <rect x="32" y="24" width="44" height="38" rx="16" fill={url("shell")} />
        <rect x="32" y="24" width="44" height="38" rx="16" fill="none" stroke="#8B86C8" strokeWidth="0.8" opacity="0.45" />
        <ellipse cx="45" cy="34" rx="12" ry="6" fill="#FFFFFF" opacity="0.28" filter={`url(#${k("soft")})`} />
        <path d="M70 38 C74 44 74 54 66 59 C72 53 71 44 68 39 Z" fill="#3F3884" opacity="0.18" />

        <rect x="37" y="33" width="34" height="21" rx="10" fill={url("visor")} />
        <rect x="37" y="33" width="34" height="21" rx="10" fill="none" stroke="#120D33" strokeWidth="0.8" />
        <rect x="40" y="35" width="28" height="6" rx="3" fill="#FFFFFF" opacity="0.1" />

        <circle cx="47" cy="43" r="6.7" fill={url("eyeGlow")} />
        <circle cx="61" cy="43" r="6.7" fill={url("eyeGlow")} />
        <g className={k("eyes")}>
          <ellipse cx="47" cy="43" rx="4.9" ry="5" fill={url("eye")} />
          <ellipse cx="61" cy="43" rx="4.9" ry="5" fill={url("eye")} />
          <circle cx="45" cy="40.8" r="1.45" fill="#FFFFFF" opacity="0.95" />
          <circle cx="59" cy="40.8" r="1.45" fill="#FFFFFF" opacity="0.95" />
        </g>
        <path d="M47.5 50.5 Q54 53.6 60.5 50.5" stroke="#8FE9F5" strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.85" />

        <g className={k("mageye")}>
          <ellipse cx="61" cy="43" rx="6.7" ry="7.2" fill={url("eye")} />
          <circle cx="58.3" cy="39.9" r="1.9" fill="#FFFFFF" opacity="0.95" />
          <circle cx="63.4" cy="45.4" r="1.1" fill="#FFFFFF" opacity="0.5" />
        </g>

        <circle className={k("core")} cx="54" cy="72.5" r="8" fill="#8B5CF6" opacity="0.35" filter={`url(#${k("softer")})`} />
        <path d="M54 65.5 C51.6 66.7 48.9 67.2 46.8 67.2 C46.8 73 48.4 77.6 54 80.2 C59.6 77.6 61.2 73 61.2 67.2 C59.1 67.2 56.4 66.7 54 65.5 Z" fill={url("coreg")} />
        <path d="M50.6 72.4 L53.2 75.1 L57.6 69.7" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />

        <g className={k("armup")}>
          <line x1="65" y1="63" x2="73.8" y2="52.6" stroke={url("shellDark")} strokeWidth="8.5" strokeLinecap="round" />
          <circle cx="65" cy="63" r="4.7" fill={url("shell")} />
          <g className={k("armfore")}>
            <line x1="73.8" y1="52.6" x2="70.6" y2="50.2" stroke={url("shellDark")} strokeWidth="7" strokeLinecap="round" />
            <circle cx="73.8" cy="52.6" r="3.7" fill={url("shell")} />
            <circle cx="61" cy="43" r="13" fill="#8B5CF6" opacity="0.12" filter={`url(#${k("softer")})`} />
            <line x1="68.5" y1="48.6" x2="73.5" y2="52.4" stroke={url("shellDark")} strokeWidth="4.6" strokeLinecap="round" />
            <line x1="68.8" y1="48.8" x2="72.9" y2="51.9" stroke={url("handle")} strokeWidth="2.6" strokeLinecap="round" />
            <ellipse cx="68.6" cy="52.4" rx="1.9" ry="2.6" fill={url("shell")} transform="rotate(44 68.6 52.4)" />
            <rect x="66.6" y="46.8" width="8" height="6.8" rx="3.4" fill={url("shell")} transform="rotate(37 70.6 50.2)" />
            <g fill={url("shell")} stroke="#7C76B6" strokeWidth="0.45" strokeOpacity="0.55">
              <rect x="68.95" y="46.6" width="2.1" height="4.2" rx="1.05" transform="rotate(127 70 48.7)" />
              <rect x="70.15" y="47.6" width="2.1" height="4.2" rx="1.05" transform="rotate(127 71.2 49.7)" />
              <rect x="71.45" y="48.5" width="2.1" height="4.2" rx="1.05" transform="rotate(127 72.5 50.6)" />
            </g>
            <circle cx="69.4" cy="48.6" r="1" fill="#FFFFFF" opacity="0.3" />
            <circle cx="61" cy="43" r="8.5" fill={url("lens")} />
            <circle cx="61" cy="43" r="10" fill="none" stroke={url("lensRim")} strokeWidth="2.9" />
            <path d="M55.4 39.6 A7.2 7.2 0 0 1 65.6 38.7" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.5" />
          </g>
        </g>

        <g className={k("fxarc")} stroke="#67E8F9" strokeLinecap="round" fill="none">
          <path d="M73.6 39.6 A13 13 0 0 1 72.3 49.5" strokeWidth="1.5" opacity="0.9" />
          <path d="M76.5 38.9 A16 16 0 0 1 74.9 51" strokeWidth="1.2" opacity="0.6" />
          <path d="M79.4 38.1 A19 19 0 0 1 77.5 52.5" strokeWidth="1" opacity="0.4" />
        </g>

        <g className={k("fx")}>
          <g className={k("fxring")}>
            <circle cx="61" cy="43" r="11.5" fill="none" stroke="#67E8F9" strokeWidth="1" strokeDasharray="2.5 4" opacity="0.85" />
          </g>
          <g className={k("fxring2")}>
            <circle cx="61" cy="43" r="8.4" fill="none" stroke="#9BF3FF" strokeWidth="0.7" strokeDasharray="1 5" opacity="0.6" />
          </g>
          <g stroke="#67E8F9" strokeWidth="1.3" strokeLinecap="round" opacity="0.85">
            <path d="M50 32 H54 M50 32 V36" />
            <path d="M72 32 H68 M72 32 V36" />
            <path d="M50 54 H54 M50 54 V50" />
            <path d="M72 54 H68 M72 54 V50" />
          </g>
          <g clipPath={`url(#${k("lensclip")})`}>
            <rect className={k("fxscan")} x="52" y="42.3" width="18" height="1.4" rx="0.7" fill="#9BF3FF" opacity="0.85" />
            <rect className={k("fxshine")} x="56.5" y="34" width="3.2" height="18" rx="1.6" fill="#FFFFFF" opacity="0.5" transform="rotate(24 61 43)" />
          </g>
        </g>
      </g>
    </svg>
  );
}
